import { TIMETABLE_FILES } from '../config/academicTerm.js';
import type { BuildingData, Floor, Room } from './floorPlans.js';

export interface CampusRoomRecord {
  id: string;
  name: string;
  type: string;
  floorId: string | null;
  floorLabel: string;
  sourceLocations: string[];
  sourceFiles: string[];
  classCount: number;
  /** Confirms the label appears in the current release, never physical placement. */
  timetableConfirmed: boolean;
  mapRoomId: string | null;
  mapFloorId: string | null;
  placement: 'schematic' | 'unmapped' | 'conflict' | 'reference-only';
}

export interface CampusTimetablePayload {
  file: string;
  data: unknown;
}

type MappedRoom = { room: Room; floor: Floor };
type JsonRecord = Record<string, unknown>;

const FLOOR_IDS: Record<string, string> = {
  basement: 'basement',
  ground: 'ground',
  'ground floor': 'ground',
  '1st floor': 'first',
  '2nd floor': 'second',
  '3rd floor': 'third',
};

/** Search normalization is punctuation-insensitive, but never performs fuzzy ID matching. */
export function normalizeCampusRoomSearch(value: string): string {
  return value.toLowerCase().normalize('NFKC').replace(/[^a-z0-9]/g, '');
}

export function matchesCampusRoom(record: CampusRoomRecord, query: string): boolean {
  const normalized = normalizeCampusRoomSearch(query);
  if (!normalized) return true;
  return [record.name, record.id, record.type, record.floorLabel, ...record.sourceLocations]
    .some(value => normalizeCampusRoomSearch(value).includes(normalized));
}

function asRecord(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonRecord
    : null;
}

function requiredArray(value: unknown, context: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`Invalid timetable room data: ${context}`);
  return value;
}

/** Walk only published schedules, not provenance copies or unrelated JSON properties. */
function collectLocations(payload: CampusTimetablePayload): string[] {
  const locations: string[] = [];
  const programs = requiredArray(asRecord(payload.data)?.programs, `${payload.file}: programs`);
  for (const program of programs) {
    for (const batch of requiredArray(asRecord(program)?.batches, `${payload.file}: batches`)) {
      for (const semester of requiredArray(asRecord(batch)?.semesters, `${payload.file}: semesters`)) {
        for (const group of requiredArray(asRecord(semester)?.groups, `${payload.file}: groups`)) {
          const schedule = asRecord(asRecord(group)?.schedule);
          if (!schedule) throw new Error(`Invalid timetable room data: ${payload.file}: schedule`);
          for (const [day, classes] of Object.entries(schedule)) {
            for (const entry of requiredArray(classes, `${payload.file}: ${day}`)) {
              const location = asRecord(entry)?.location;
              if (typeof location !== 'string' || !location.trim()) {
                throw new Error(`Missing timetable location: ${payload.file}: ${day}`);
              }
              locations.push(location);
            }
          }
        }
      }
    }
  }
  return locations;
}

function parseLocation(raw: string) {
  const parts = raw.trim().split(/\s+-\s+/);
  const floorLabel = parts.length >= 3 ? parts[0].trim() : 'Location not specified';
  const printedType = parts.length >= 3 ? parts[1].trim() : 'Location';
  const name = parts.length >= 3 ? parts.slice(2).join(' - ').trim() : raw.trim();
  // A building label or an online room number is not evidence of a floor.
  const isAcademicBlockII = /^Academic Block II-\d+$/i.test(name);
  const floorId = isAcademicBlockII && floorLabel.toLowerCase() === '2nd floor'
    ? 'academic-block-ii-second'
    : FLOOR_IDS[floorLabel.toLowerCase()] ?? null;
  const type = floorLabel.toLowerCase() === 'online' ? 'Online session' : ({
    class: 'Classroom',
    auditorium: 'Lecture Theatre',
    lab: 'Laboratory',
  }[printedType.toLowerCase()] ?? printedType);
  return {
    floorId,
    floorLabel: isAcademicBlockII ? `Academic Block II · ${floorLabel}` : floorLabel,
    name,
    type,
  };
}

function matchMapRoom(name: string, mappedRooms: MappedRoom[]): MappedRoom | null {
  const normalized = normalizeCampusRoomSearch(name);
  const direct = mappedRooms.filter(({ room }) =>
    normalizeCampusRoomSearch(room.roomId) === normalized ||
    normalizeCampusRoomSearch(room.name) === normalized);
  // Never choose arbitrarily if a future map contains a duplicate ID/name.
  if (direct.length > 0) return direct.length === 1 ? direct[0] : null;

  // These are explicit label reconciliations, not substring/code-only matches.
  // General 2 is a possible legacy identity only: its conflicting floor prevents
  // using that geometry. This does NOT verify that the two physical labs are one.
  const aliases: Record<string, string> = {
    psychologylablr208: 'LR-208',
    general2: 'GEN-LAB-2',
  };
  const alias = aliases[normalized];
  const candidates = alias ? mappedRooms.filter(({ room }) => room.roomId === alias) : [];
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * Reconciles labels with the existing drawing without creating/moving coordinates.
 * `schematic` means a same-floor legacy drawing exists; it is still unmeasured.
 * Academic Block II IDs remain distinct from similarly numbered LR rooms.
 */
export function buildCampusRoomDirectory(
  data: BuildingData,
  payloads: CampusTimetablePayload[],
): CampusRoomRecord[] {
  const mappedRooms = data.floors.flatMap(floor => floor.rooms.map(room => ({ room, floor })));
  const grouped = new Map<string, CampusRoomRecord>();
  const matchedMapIds = new Set<string>();
  const usedIds = new Set<string>();
  const seenFiles = new Set<string>();

  for (const payload of payloads) {
    if (!(TIMETABLE_FILES as readonly string[]).includes(payload.file)) {
      throw new Error(`Not a current timetable source: ${payload.file}`);
    }
    if (seenFiles.has(payload.file)) throw new Error(`Duplicate timetable source: ${payload.file}`);
    seenFiles.add(payload.file);
    for (const raw of collectLocations(payload)) {
      // Only case/whitespace aliases of the full printed location are deduplicated.
      // Different buildings, floors, room types, or codes remain separate records.
      const key = raw.trim().toLowerCase().replace(/\s+/g, ' ');
      const previous = grouped.get(key);
      if (previous) {
        previous.classCount += 1;
        if (!previous.sourceLocations.includes(raw)) previous.sourceLocations.push(raw);
        if (!previous.sourceFiles.includes(payload.file)) previous.sourceFiles.push(payload.file);
        continue;
      }

      const parsed = parseLocation(raw);
      // Do not match 'Online - Class - 214' to any physical room named 214.
      const mapped = parsed.floorId ? matchMapRoom(parsed.name, mappedRooms) : null;
      const desiredId = mapped?.room.roomId ?? `location:${key}`;
      const id = usedIds.has(desiredId) ? `location:${key}` : desiredId;
      usedIds.add(id);
      if (mapped) matchedMapIds.add(mapped.room.roomId);
      grouped.set(key, {
        id,
        ...parsed,
        sourceLocations: [raw],
        sourceFiles: [payload.file],
        classCount: 1,
        timetableConfirmed: true,
        mapRoomId: mapped?.room.roomId ?? null,
        mapFloorId: mapped?.floor.id ?? null,
        placement: !mapped ? 'unmapped' : mapped.floor.id === parsed.floorId ? 'schematic' : 'conflict',
      });
    }
  }

  const references: CampusRoomRecord[] = mappedRooms
    .filter(({ room }) => !matchedMapIds.has(room.roomId))
    .map(({ room, floor }) => ({
      id: usedIds.has(room.roomId) ? `reference:${floor.id}:${room.roomId}` : room.roomId,
      name: room.name,
      type: room.type,
      floorId: floor.id,
      floorLabel: floor.label,
      sourceLocations: [],
      sourceFiles: [],
      classCount: 0,
      timetableConfirmed: false,
      mapRoomId: room.roomId,
      mapFloorId: floor.id,
      placement: 'reference-only',
    }));

  return [...grouped.values(), ...references].sort((a, b) =>
    Number(b.timetableConfirmed) - Number(a.timetableConfirmed) ||
    a.floorLabel.localeCompare(b.floorLabel, undefined, { numeric: true }) ||
    a.name.localeCompare(b.name, undefined, { numeric: true }));
}

let payloadPromise: Promise<CampusTimetablePayload[]> | null = null;

export async function loadCampusRoomDirectory(data: BuildingData): Promise<CampusRoomRecord[]> {
  if (!payloadPromise) {
    payloadPromise = Promise.all(TIMETABLE_FILES.map(async file => {
      const response = await fetch(`/${file}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Could not load campus room source: ${file} (${response.status})`);
      return { file, data: await response.json() as unknown };
    })).catch(error => {
      payloadPromise = null;
      throw error;
    });
  }
  try {
    return buildCampusRoomDirectory(data, await payloadPromise);
  } catch (error) {
    // A corrected source must be fetched again after either a network or validation failure.
    payloadPromise = null;
    throw error;
  }
}
